// #3708 — the UX Design agent wrote Screen Specifications or a Component Catalogue in chat and
// BluePrint did not save it. Before this the user saw nothing. One notice per artefact type
// (AVR ruling 4); each notice recovers its own type, from the agent's own message (ruling 2).
import { useState } from 'react';
import { apiFetch } from '../../lib/api';

export type NotSavedArtefactType = 'screen_spec' | 'component_catalogue';
// #3835 — 'approval_turn': the reply carried the document and approved it, with nothing saved yet.
// #3851 — 'reply_incomplete': the reply was cut off (stopped, or out of length), so it is never saved.
export type NotSavedReason = 'no_structured_block' | 'no_content' | 'detector_missed' | 'save_failed' | 'approval_turn' | 'reply_incomplete';

export interface ArtefactNotSavedInfo {
  artefactType: NotSavedArtefactType;
  reason: NotSavedReason;
  savedVersionExists: boolean;
  recoverable: boolean;
}

export type RecoverOutcome =
  | { status: 'recovered'; artefactType: NotSavedArtefactType; artefactId: string; title: string; version: string }
  | { status: 'not_extractable'; artefactType: NotSavedArtefactType; reason: 'no_content' | 'no_structured_block' | 'invalid_structured_block' }
  | { status: 'superseded'; artefactType: NotSavedArtefactType; reason: 'newer_version' | 'approved' | 'handed_off' | 'removed'; artefactId: string | null; version: string | null }
  | { status: 'source_missing' | 'nothing_to_recover' | 'locked' | 'try_again'; artefactType: NotSavedArtefactType };

/** The outcomes after which the notice has nothing left to offer and closes (the server cleared the slot). */
export function closesNotice(outcome: RecoverOutcome): boolean {
  return outcome.status === 'recovered' || outcome.status === 'superseded' || outcome.status === 'nothing_to_recover';
}

const TYPES: readonly string[] = ['screen_spec', 'component_catalogue'];
const REASONS: readonly string[] = ['no_structured_block', 'no_content', 'detector_missed', 'save_failed', 'approval_turn', 'reply_incomplete'];
/** The reasons whose document is in the agent's message, so Recover can re-extract it (server: isRecoverableReason). */
const RECOVERABLE_REASONS: readonly string[] = ['detector_missed', 'save_failed', 'approval_turn'];

export const NOT_SAVED_LABEL: Record<NotSavedArtefactType, string> = {
  screen_spec: 'Screen Specifications',
  component_catalogue: 'Component Catalogue',
};

export function parseArtefactNotSavedEvent(data: {
  type: string;
  artefactType?: unknown;
  reason?: unknown;
  savedVersionExists?: unknown;
  recoverable?: unknown;
}): ArtefactNotSavedInfo | null {
  if (data.type !== 'artefact_not_saved') return null;
  if (typeof data.artefactType !== 'string' || !TYPES.includes(data.artefactType)) return null;
  if (typeof data.reason !== 'string' || !REASONS.includes(data.reason)) return null;
  return {
    artefactType: data.artefactType as NotSavedArtefactType,
    reason: data.reason as NotSavedReason,
    savedVersionExists: data.savedVersionExists === true,
    recoverable: data.recoverable === true,
  };
}

/**
 * #3708 (R1 P1-1) — the notices to show on load, from the session's per-type slots
 * (metadata.lastSaveBlockByType). Only fixed codes are read; anything else is ignored.
 */
export function notSavedFromSessionMetadata(metadata: { lastSaveBlockByType?: unknown } | null | undefined): Partial<Record<NotSavedArtefactType, ArtefactNotSavedInfo>> {
  const out: Partial<Record<NotSavedArtefactType, ArtefactNotSavedInfo>> = {};
  const slots = metadata?.lastSaveBlockByType;
  if (!slots || typeof slots !== 'object') return out;
  for (const t of TYPES as NotSavedArtefactType[]) {
    const n = (slots as Record<string, unknown>)[t] as Record<string, unknown> | undefined;
    if (!n || typeof n !== 'object' || n.kind !== 'not_saved' || n.artefactType !== t) continue;
    if (typeof n.reason !== 'string' || !REASONS.includes(n.reason)) continue;
    const reason = n.reason as NotSavedReason;
    out[t] = {
      artefactType: t,
      reason,
      savedVersionExists: n.savedVersionExists === true,
      recoverable: n.recoverable === true && RECOVERABLE_REASONS.includes(reason),
    };
  }
  return out;
}

export function notSavedReasonText(info: Pick<ArtefactNotSavedInfo, 'artefactType' | 'reason'>): string {
  const label = NOT_SAVED_LABEL[info.artefactType];
  switch (info.reason) {
    case 'detector_missed':
      return `The reply looked like the ${label}, but it did not have the shape BluePrint saves automatically.`;
    case 'no_structured_block':
      return `The reply has no valid JSON code block for the ${label}, and BluePrint could not build one.`;
    case 'no_content':
      return `The reply was marked complete, but it does not contain the ${label} document.`;
    case 'save_failed':
      return 'The save failed on the server.';
    case 'approval_turn':
      return `The reply also approved the ${label}, but a reply that approves is never saved and there was no saved version to approve.`;
    case 'reply_incomplete':
      return `The reply was cut off before it finished, so the ${label} document in it is incomplete and was not saved.`;
  }
}

export function recoverOutcomeText(outcome: RecoverOutcome): string {
  const label = NOT_SAVED_LABEL[outcome.artefactType];
  switch (outcome.status) {
    case 'recovered':
      return `Recovered: the ${label} were saved as v${outcome.version} (draft).`;
    case 'not_extractable':
      if (outcome.reason === 'invalid_structured_block') {
        return `Could not recover: the JSON code block in the agent's message does not have a valid ${label} shape. Ask the agent to produce the ${label} again.`;
      }
      return outcome.reason === 'no_structured_block'
        ? `Could not recover: the agent's message has no valid JSON code block for the ${label}. Ask the agent to produce the ${label} again.`
        : `Could not recover: the agent's message does not contain the ${label}. Ask the agent to produce the ${label} again.`;
    case 'superseded': {
      const v = outcome.version ? ` (v${outcome.version})` : '';
      if (outcome.reason === 'approved') return `Not recovered: the ${label} were approved${v} after this reply, so the approved version was kept.`;
      if (outcome.reason === 'handed_off') return `Not recovered: the ${label}${v} have been handed off, so they were kept as they are.`;
      if (outcome.reason === 'removed') return `Not recovered: the ${label} this reply was meant to update no longer exist. Ask the agent to produce them again.`;
      return `Not recovered: a newer version of the ${label}${v} was saved after this reply, so it was kept.`;
    }
    case 'try_again':
      return `Could not check the saved ${label} just now, so nothing was written. Try Recover again in a moment.`;
    case 'source_missing':
      return `Could not recover: the agent's message is no longer in this chat. Ask the agent to produce the ${label} again.`;
    case 'nothing_to_recover':
      return `Nothing to recover: there is no unsaved ${label} reply on record for this chat.`;
    case 'locked':
      return `Could not recover: the ${label} are handed off and locked. Changes now need a new handoff cycle or an amendment request.`;
  }
}

export function ArtefactNotSavedBanner({
  info,
  sessionId,
  onRecovered,
  onDismiss,
  onClosed,
  canRecover = true,
}: {
  info: ArtefactNotSavedInfo;
  sessionId: string;
  onRecovered: (outcome: Extract<RecoverOutcome, { status: 'recovered' }>) => void;
  onDismiss: () => void;
  /** The server cleared the slot without saving (superseded / nothing to recover): show why, then close. */
  onClosed?: (message: string) => void;
  /** False on a read-only (archived / completed) session: the server refuses Recover there, so it is not offered. */
  canRecover?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const label = NOT_SAVED_LABEL[info.artefactType];

  const recover = async () => {
    setBusy(true);
    setResult(null);
    try {
      const outcome = await apiFetch<RecoverOutcome>(
        `/sessions/${sessionId}/artefacts/${info.artefactType}/recover`,
        { method: 'POST' },
      );
      if (outcome.status === 'recovered') {
        onRecovered(outcome);
        return;
      }
      if (closesNotice(outcome) && onClosed) {
        onClosed(recoverOutcomeText(outcome));
        return;
      }
      setResult(recoverOutcomeText(outcome));
    } catch (e: unknown) {
      setResult(`Recovery failed: ${e instanceof Error ? e.message : 'unknown error'}. Ask the agent to produce the ${label} again.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="alert"
      data-testid={`artefact-not-saved-${info.artefactType}`}
      className="mx-6 mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex-shrink-0"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold">The {label} were not saved.</p>
        <button type="button" onClick={onDismiss} aria-label={`Dismiss the ${label} notice`} className="text-amber-800 hover:opacity-70">
          ×
        </button>
      </div>
      <p className="mt-1">
        {notSavedReasonText(info)}{' '}
        {info.savedVersionExists
          ? `The saved ${label} are unchanged.`
          : `No ${label} are saved, so this stage is not complete.`}
      </p>
      <p className="mt-1">
        {info.recoverable
          ? `Recover saves the ${label} from the agent's message, or ask the agent to produce them again.`
          : `Ask the agent to produce the ${label} again in full (one message, with the JSON code block).`}
      </p>
      {info.recoverable && canRecover && (
        <button
          type="button"
          onClick={() => void recover()}
          disabled={busy}
          className="mt-2 px-3 py-1 rounded-md text-xs font-medium border border-amber-400 bg-white text-amber-800 disabled:opacity-60"
        >
          {busy ? 'Recovering…' : `Recover ${label}`}
        </button>
      )}
      {result && <p className="mt-2" data-testid="artefact-not-saved-result">{result}</p>}
    </div>
  );
}
